import { useMutation } from "@apollo/client/react";
import mixpanel from "mixpanel-browser";
import {
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";
import { v4 as uuidv4 } from "uuid";

import { uploadToCloud } from "../api/UploadAudio";
import { alert } from "../components/common/Alert";
import { db } from "../db";
import { LOG_RECORDING_CONSENT } from "../graphql/mutations/RecordingConsent";

const syncNotesContext = createContext();

export function useSyncNotes() {
  return useContext(syncNotesContext);
}

function useProvideSyncNotes() {
  const [syncNotesQueue, setSyncNotesQueue] = useState([]);
  const [uploadIndex, setUploadIndex] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({
    numChunksTotal: 0,
    numChunksCompleted: 0,
  });
  const [logRecordingConsent] = useMutation(LOG_RECORDING_CONSENT);

  useEffect(() => {
    async function processQueue() {
      if (!syncNotesQueue.length || uploadIndex !== null) return;

      const currentItem = syncNotesQueue[0];
      setUploadIndex(currentItem.id);

      try {
        const syncedRecordingUUID = uuidv4();

        // Consent affirmed offline (see Record.jsx): log the deferred audit
        // event before anything else, so the note is never created before its
        // consent event exists. If this write fails the whole sync fails and
        // can be retried.
        let consentEventUuid = currentItem.consentEventUuid ?? null;
        if (!consentEventUuid && currentItem.pendingConsent) {
          // Re-read the persisted record first: a previous sync attempt may
          // already have logged the event (and then failed at upload), and the
          // queued item could be a stale copy. Never log a duplicate.
          const storedRecording = await db.unsyncedRecordings.get(
            currentItem.id,
          );
          consentEventUuid =
            storedRecording?.consentEventUuid ?? null;
        }
        if (!consentEventUuid && currentItem.pendingConsent) {
          const pendingConsent = currentItem.pendingConsent;
          const { data } = await logRecordingConsent({
            variables: {
              action: "ACKNOWLEDGED",
              source: "WEB",
              surface: pendingConsent.surface,
              ...(pendingConsent.acknowledgedAt && {
                createdAt: pendingConsent.acknowledgedAt,
              }),
              ...(pendingConsent.appendToNoteUuid && {
                noteUuid: pendingConsent.appendToNoteUuid,
              }),
            },
          });
          consentEventUuid =
            data?.logRecordingConsent?.consentEventUuid;
          if (!consentEventUuid) {
            throw new Error(
              "Recording consent event was not created.",
            );
          }
          // Idempotency: persist the event uuid BEFORE note creation, so a
          // retried sync (e.g. after a failed upload) reuses this event
          // instead of logging a duplicate.
          await db.unsyncedRecordings.update(currentItem.id, {
            consentEventUuid,
            pendingConsent: null,
          });
          mixpanel.track("Recording Consent Synced Offline", {
            recordingId: currentItem.id,
            surface: pendingConsent.surface,
          });
        }

        const response = await uploadToCloud({
          blob: currentItem.blob,
          createNewNote: currentItem.createNewNote,
          jobType: currentItem.jobType,
          recordingAs: currentItem.recordingAs,
          title: currentItem.title,
          syncedRecordingUUID,
          uploadStatus,
          setUploadStatus,
          recordingConsentEventUuid: consentEventUuid,
        });

        if (!response?.success) throw new Error("Upload failed.");

        // Successfully uploaded, now delete the recording from the database
        await db.unsyncedRecordings.delete(currentItem.id);

        currentItem.onSuccess?.(response);
        alert("success", "Recording synced successfully!");
      } catch (error) {
        alert("warning", "Sync failed. Please try again.");
        console.error(error);
        mixpanel.track("Recording Sync Failed", {
          recordingId: currentItem.id,
          error: error.message,
        });
      } finally {
        setSyncNotesQueue((prev) => prev.slice(1)); // Update the queue
        setUploadIndex(null);
      }
    }

    processQueue();
  }, [
    syncNotesQueue,
    uploadIndex,
    uploadStatus,
    logRecordingConsent,
  ]);

  const addToSyncNotesQueue = (item, createNewNote, onSuccess) => {
    setSyncNotesQueue((prev) => {
      if (prev.some((queuedItem) => queuedItem.id === item.id))
        return prev; // Prevent duplicates
      return [...prev, { ...item, createNewNote, onSuccess }];
    });
  };

  return {
    syncNotesQueue,
    uploadIndex,
    uploadStatus,
    addToSyncNotesQueue,
  };
}

export function SyncNotesProvider({ children }) {
  const syncNotes = useProvideSyncNotes();

  return (
    <syncNotesContext.Provider value={syncNotes}>
      {children}
    </syncNotesContext.Provider>
  );
}
